import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Mail, Phone, MessageSquare } from "lucide-react";

import { publicApi } from "@/lib/api";
import { TIERS } from "@/lib/tiers";
import PhotoCategoryUploader from "@/components/PhotoCategoryUploader";
import HostAvatarUploader from "@/components/HostAvatarUploader";
import BrandBriefSection from "@/components/BrandBriefSection";
import StageTimeline from "@/components/StageTimeline";
import ContactDialog from "@/components/ContactDialog";

const TIER_NAME_BY_ID = Object.fromEntries(TIERS.map((t) => [t.id, t.name]));

const SUCCESS_IMG =
  "https://images.pexels.com/photos/13722886/pexels-photo-13722886.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Success() {
  const [params] = useSearchParams();
  const id = params.get("id");
  const [data, setData] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    publicApi
      .get(`/submissions/${id}`)
      .then((res) => setData(res.data))
      .catch(() => {});
  }, [id]);

  return (
    <main data-testid="success-page" className="bg-[#FAF9F6] min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Left column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#C03028]/30 rounded-none">
              <Check size={14} className="text-[#C03028]" />
              <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C03028]">
                Application received
              </span>
            </div>

            <h1
              data-testid="success-headline"
              className="font-serif-display mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2A1F1A]"
            >
              Thank you — we&apos;ll be
              <br />
              <span className="italic text-[#C03028]">in touch shortly.</span>
            </h1>

            <p className="mt-7 text-[#6E6E70] leading-relaxed max-w-xl">
              Your application is with our studio. To begin our review, please
              share the six photos below — and feel free to write us a note
              about anything we should know.
            </p>

            {data && (
              <div
                data-testid="success-summary"
                className="mt-10 border border-[#E3DED5] bg-white p-6 max-w-xl"
              >
                <div className="eyebrow">Reference</div>
                <div className="font-mono text-sm text-[#2A1F1A] mt-2 break-all">
                  {data.id}
                </div>

                <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-[#6E6E70] text-xs uppercase tracking-[0.18em]">
                      Service level
                    </div>
                    <div className="text-[#2A1F1A] mt-1">
                      {TIER_NAME_BY_ID[data.tier] || data.tier}
                    </div>
                  </div>
                  <div>
                    <div className="text-[#6E6E70] text-xs uppercase tracking-[0.18em]">
                      Property
                    </div>
                    <div className="text-[#2A1F1A] mt-1 truncate">
                      {data.property_name}
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="eyebrow">Your application stage</div>
                  <div className="mt-4">
                    <StageTimeline
                      currentStatus={data.status || "application_received"}
                    />
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Right column — image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 lg:sticky lg:top-24"
          >
            <div className="relative grain-overlay overflow-hidden">
              <img
                src={SUCCESS_IMG}
                alt="A quiet, considered space"
                className="w-full h-[460px] lg:h-[560px] object-cover"
              />
            </div>

            {data && (
              <div className="mt-4 bg-white border border-[#E3DED5] p-5">
                <div className="eyebrow">Need to reach us?</div>
                <ul className="mt-3 space-y-2 text-sm text-[#6E6E70]">
                  <li className="flex items-center gap-2">
                    <Mail size={14} className="text-[#C03028]" /> hello@sasha.host
                  </li>
                  <li className="flex items-center gap-2">
                    <Phone size={14} className="text-[#C03028]" /> +351 000 000 000
                  </li>
                </ul>
                <button
                  type="button"
                  onClick={() => setContactOpen(true)}
                  data-testid="open-contact-dialog"
                  className="btn-primary mt-4 inline-flex items-center gap-2 px-4 py-2.5 text-sm tracking-wide rounded-none w-full justify-center"
                >
                  <MessageSquare size={14} /> Write to the studio
                </button>
              </div>
            )}
          </motion.div>
        </div>

        {/* Photo upload — full width */}
        {id && (
          <div className="mt-12 space-y-6">
            <HostAvatarUploader submissionId={id} />
            <PhotoCategoryUploader submissionId={id} />
          </div>
        )}

        {/* Gold-only brand brief */}
        {id && data?.tier === "premium" && (
          <div className="mt-8">
            <BrandBriefSection
              submissionId={id}
              initialBrief={data?.avatar_brief || ""}
            />
          </div>
        )}

        {/* Closing actions */}
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Link
            to="/"
            data-testid="success-home-cta"
            className="btn-secondary inline-flex items-center justify-center gap-2 px-6 py-3 text-sm tracking-wide rounded-none"
          >
            Return home
          </Link>
          <Link
            to="/investing"
            data-testid="success-investing-cta"
            className="btn-secondary inline-flex items-center justify-center gap-2 px-6 py-3 text-sm tracking-wide rounded-none"
          >
            Explore Janté Lux investing
          </Link>
        </div>
      </div>

      <ContactDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        submissionId={id}
        defaultName={data?.full_name || ""}
        defaultEmail={data?.email || ""}
      />
    </main>
  );
}
